import React, { useState, useContext } from "react";
import * as S from "./NewContractWithdrawStyle";
import { AuthContext } from "../../../Context/AuthContext";
import { formatCPF } from "../../ASSETS/assets";
import { useMessage } from "../../../Context/MessageContext";
import { contractAdminService } from "../../../Services/contractAdminService"; // Importando o serviço
import {
  X,
  Search,
  Loader2,
  Wallet,
  CheckCircle2,
  ToggleLeft,
  ToggleRight,
  ArrowRight,
  UserCheck,
} from "lucide-react";
import Loading from "../../Loader";

export default function NewContractWithdraw({ handleClose }) {
  const { showMessage } = useMessage();
  const { getClients, clientsDataBase } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // Estados da Operação
  const [selectedClient, setSelectedClient] = useState(null);
  const [operationType, setOperationType] = useState("only_repurchase");
  const [contractsToBuy, setContractsToBuy] = useState(10);
  const [extraWithdraw, setExtraWithdraw] = useState(0);
  const [isManual, setIsManual] = useState(false);
  const [manualPercentage, setManualPercentage] = useState(0);

  // Função de Busca de Cliente
  const handleSearchClient = async () => {
    const term = searchTerm.trim();
    if (!term) return;

    setSearchLoading(true);
    setHasSearched(true);
    try {
      await getClients(20, 0, term, "asc", "");
    } catch (error) {
      showMessage("Erro ao buscar clientes no servidor.", "error");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearchTerm("");
    setHasSearched(false);
  };

  const handleConfirm = async () => {
    if (!selectedClient)
      return showMessage("Selecione um cliente beneficiário.", "warning");
    if (contractsToBuy < 10)
      return showMessage("A quantidade mínima é de 10 contratos.", "warning");

    setLoading(true);
    try {
      if (operationType === "only_repurchase") {
        // Chamada via Serviço: Apenas Recompra
        await contractAdminService.repurchaseWithBalanceAdmin({
          clientId: selectedClient.id,
          contractsToBuy: parseInt(contractsToBuy),
        });
      } else {
        // Chamada via Serviço: Recompra + Saque
        await contractAdminService.repurchaseAndWithdraw({
          clientId: selectedClient.id,
          contractsToBuy: parseInt(contractsToBuy),
          extraWithdrawAmount: parseFloat(extraWithdraw),
          contractValorizationManual: isManual,
          manualMonthlyPercentage: isManual
            ? parseFloat(manualPercentage)
            : null,
        });
      }

      showMessage("Operação realizada com sucesso!", "success");
      handleClose();
    } catch (error) {
      const msg =
        error.response?.data?.message || "Erro ao processar reversão de saque.";
      showMessage(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <S.Overlay>
      <Loading load={loading} />

      <S.Container>
        <S.Header>
          <h2>
            <span>Golden</span> Reversão de Saque
          </h2>
          <S.CloseBtn onClick={handleClose}>
            <X size={20} />
          </S.CloseBtn>
        </S.Header>

        <S.Body>
          {/* PESQUISA DE CLIENTE */}
          <S.SearchSection>
            <label>Identificar Cliente Beneficiário</label>
            <S.SearchBar>
              <input
                placeholder="Pesquise por Nome ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearchClient()}
              />
              <S.SearchBtn
                onClick={handleSearchClient}
                disabled={searchLoading}
              >
                {searchLoading ? (
                  <Loader2 size={20} className="spinning" />
                ) : (
                  <Search size={20} />
                )}
              </S.SearchBtn>
            </S.SearchBar>

            {/* RESULTADOS DA PESQUISA */}
            {(searchLoading || (hasSearched && !selectedClient)) && (
              <S.ResultsList>
                {searchLoading ? (
                  <S.SearchLoadingText>
                    <Loader2 size={20} className="spinning" />
                    Consultando base de dados...
                  </S.SearchLoadingText>
                ) : clientsDataBase?.length > 0 ? (
                  clientsDataBase.map((client) => (
                    <S.ResultItem
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <UserCheck size={18} color="#d4af37" />
                        <span className="name">{client.name}</span>
                      </div>
                      <span className="cpf">{formatCPF(client.cpfCnpj)}</span>
                    </S.ResultItem>
                  ))
                ) : (
                  <S.SearchLoadingText>
                    Nenhum resultado para "{searchTerm}"
                  </S.SearchLoadingText>
                )}
              </S.ResultsList>
            )}
          </S.SearchSection>

          {/* CLIENTE SELECIONADO */}
          {selectedClient && (
            <S.SelectedClientBox>
              <div className="info">
                <strong>{selectedClient.name}</strong>
                <span>
                  ID: {selectedClient.id} • CPF:{" "}
                  {formatCPF(selectedClient.cpfCnpj)}
                </span>
              </div>
              <CheckCircle2 size={28} color="#27ae60" />
            </S.SelectedClientBox>
          )}

          {/* CONFIGURAÇÃO DA OPERAÇÃO */}
          <S.Card>
            <label>Definição da Operação Financeira</label>
            <S.ModeGrid>
              <S.ModeOption
                active={operationType === "only_repurchase"}
                onClick={() => setOperationType("only_repurchase")}
              >
                <strong>Recompra Pura</strong>
                <small>Reinvestimento Integral</small>
              </S.ModeOption>
              <S.ModeOption
                active={operationType === "with_withdraw"}
                onClick={() => setOperationType("with_withdraw")}
              >
                <strong>Recompra + Saque</strong>
                <small>Gerar Saque Residual</small>
              </S.ModeOption>
            </S.ModeGrid>

            <S.InputGrid>
              <S.FormGroup>
                <label>Qtd. de Contratos (Cotas)</label>
                <input
                  type="number"
                  value={contractsToBuy}
                  onChange={(e) => setContractsToBuy(e.target.value)}
                  min="10"
                />
              </S.FormGroup>

              {operationType === "with_withdraw" && (
                <S.FormGroup>
                  <label>Saque Extra (R$)</label>
                  <input
                    type="number"
                    placeholder="0,00"
                    value={extraWithdraw}
                    onChange={(e) => setExtraWithdraw(e.target.value)}
                  />
                </S.FormGroup>
              )}
            </S.InputGrid>

            {/* VALORIZAÇÃO MANUAL */}
            {operationType === "with_withdraw" && (
              <>
                <S.ToggleRow onClick={() => setIsManual(!isManual)}>
                  {isManual ? (
                    <ToggleRight size={34} color="#d4af37" />
                  ) : (
                    <ToggleLeft size={34} color="#ccc" />
                  )}
                  Habilitar Porcentagem Manual
                </S.ToggleRow>

                {isManual && (
                  <S.FormGroup style={{ marginTop: "15px", maxWidth: "240px" }}>
                    <label>Rentabilidade Mensal (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Ex: 4.5"
                      value={manualPercentage}
                      onChange={(e) => setManualPercentage(e.target.value)}
                    />
                  </S.FormGroup>
                )}
              </>
            )}
          </S.Card>
        </S.Body>

        <S.Footer>
          <S.SubmitBtn
            onClick={handleConfirm}
            disabled={!selectedClient || loading}
          >
            Finalizar e Gerar Contrato <ArrowRight size={20} />
          </S.SubmitBtn>
        </S.Footer>
      </S.Container>
    </S.Overlay>
  );
}
